import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

export function AuthenticationPage() {
  useDocumentMeta({ title: 'Authentication & Teams: LogPulse Docs', description: 'Manage authentication, API keys, roles, and team access in LogPulse.' });

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'email-password', label: 'Email & Password' },
    { id: 'password-requirements', label: 'Password Requirements', level: 1 },
    { id: 'password-reset', label: 'Password Reset', level: 1 },
    { id: 'social-login', label: 'Social Login (OAuth)' },
    { id: 'google-oauth', label: 'Google OAuth', level: 1 },
    { id: 'github-oauth', label: 'GitHub OAuth', level: 1 },
    { id: 'sso-saml', label: 'SSO / SAML (Planned)' },
    { id: 'api-keys', label: 'API Keys' },
    { id: 'key-scopes', label: 'Key Scopes', level: 1 },
    { id: 'key-management', label: 'Creating and Managing Keys', level: 1 },
    { id: 'key-revocation', label: 'Revoking Keys', level: 1 },
    { id: 'team-management', label: 'Team Management' },
    { id: 'inviting-members', label: 'Inviting Members', level: 1 },
    { id: 'roles', label: 'Roles', level: 1 },
    { id: 'role-permissions', label: 'Role Permissions' },
    { id: 'organizations', label: 'Organizations' },
    { id: 'creating-orgs', label: 'Creating Organizations', level: 1 },
    { id: 'switching-orgs', label: 'Switching Organizations', level: 1 },
    { id: 'org-settings', label: 'Organization-Level Settings', level: 1 },
    { id: 'audit-log', label: 'Audit Log' },
    { id: 'session-management', label: 'Session Management' },
    { id: 'security-best-practices', label: 'Security Best Practices' },
    { id: 'enterprise-access', label: 'Enterprise Access Controls (Roadmap)' },
    { id: 'scim', label: 'SCIM Provisioning', level: 1 },
    { id: 'jit-access', label: 'Just-in-Time Access', level: 1 },
    { id: 'ip-allowlists', label: 'IP Allowlists', level: 1 },
    { id: 'cmk', label: 'Customer-Managed Keys', level: 1 },
    { id: 'audit-export', label: 'Audit Log Export', level: 1 },
  ];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <Section id="overview" title="Authentication & Team Management">
          <P>
            LogPulse supports multiple authentication methods to fit your organization's
            security requirements. Users can sign in with email and password, OAuth social
            login (Google and GitHub), or API keys for programmatic access.
          </P>
          <P>
            Access control is managed through a role-based system with four levels: Owner,
            Admin, Member, and Viewer. Organizations provide multi-tenant isolation for
            companies managing multiple teams or products.
          </P>
          <Table
            headers={['Auth Method', 'Use Case', 'MFA Support']}
            rows={[
              ['Email & Password', 'Individual accounts and small teams', 'Not yet available'],
              ['OAuth (Google)', 'Teams using Google Workspace for identity', 'Delegated to Google'],
              ['OAuth (GitHub)', 'Developer teams using GitHub for identity', 'Delegated to GitHub'],
              ['API Keys', 'Programmatic access, CI/CD pipelines, agent configuration', 'N/A (key-based)'],
            ]}
          />
        </Section>

        <Section id="email-password" title="Email & Password">
          <P>
            Standard email and password authentication is available for all LogPulse accounts.
            New users create an account with an email address and password, then verify their
            email address via a confirmation link.
          </P>
          <SubSection id="password-requirements" title="Password Requirements">
            <P>Passwords must meet the following requirements:</P>
            <Table
              headers={['Requirement', 'Details']}
              rows={[
                ['Minimum length', '12 characters'],
                ['Character classes', 'At least one uppercase letter, one lowercase letter, and one number'],
                ['Special characters', 'Recommended but not required'],
                ['Breached password check', 'Passwords are checked against the HaveIBeenPwned database and rejected if found'],
                ['Password history', 'Cannot reuse any of your last 10 passwords'],
                ['Expiration', 'No forced expiration (per NIST SP 800-63B guidelines)'],
              ]}
            />
          </SubSection>
          <SubSection id="password-reset" title="Password Reset">
            <P>
              To reset a forgotten password, click "Forgot password" on the login page. A
              password reset link is sent to the registered email address. The link expires
              after 1 hour and can only be used once.
            </P>
            <Callout type="info">
              If you signed up using Google or GitHub OAuth, password reset is managed by your
              identity provider. Use the LogPulse password reset flow only for email/password accounts.
            </Callout>
          </SubSection>
        </Section>

        <Section id="social-login" title="Social Login (OAuth)">
          <P>
            LogPulse supports OAuth social login with Google and GitHub. Users can sign in
            with their existing Google or GitHub accounts without needing to create a separate
            LogPulse password.
          </P>

          <SubSection id="google-oauth" title="Google OAuth">
            <P>
              To enable Google sign-in, configure your Google OAuth credentials in the
              environment variables. Users with Google Workspace accounts can sign in directly.
            </P>
            <Table
              headers={['Environment Variable', 'Description']}
              rows={[
                ['GOOGLE_CLIENT_ID', 'Google OAuth client ID from Google Cloud Console'],
                ['GOOGLE_CLIENT_SECRET', 'Google OAuth client secret'],
              ]}
            />
          </SubSection>

          <SubSection id="github-oauth" title="GitHub OAuth">
            <P>
              To enable GitHub sign-in, configure your GitHub OAuth App credentials. This is
              especially convenient for developer teams already using GitHub.
            </P>
            <Table
              headers={['Environment Variable', 'Description']}
              rows={[
                ['GITHUB_CLIENT_ID', 'GitHub OAuth App client ID'],
                ['GITHUB_CLIENT_SECRET', 'GitHub OAuth App client secret'],
              ]}
            />
          </SubSection>
        </Section>

        <Section id="sso-saml" title="SSO / SAML (Coming Soon)">
          <Callout type="info">
            SAML 2.0 SSO is a planned feature and is not yet available. LogPulse currently
            supports OAuth-based social login with Google and GitHub for centralized
            authentication. Enterprise SAML integration is on the roadmap.
          </Callout>
          <P>
            When available, SAML SSO will support:
          </P>
          <Table
            headers={['Capability', 'Status', 'Details']}
            rows={[
              ['SAML 2.0 authentication', 'Planned', 'SP-initiated and IdP-initiated SSO flows'],
              ['SCIM provisioning', 'Planned', 'Automatic user creation and deactivation from IdP'],
              ['Just-in-Time (JIT) provisioning', 'Planned', 'Auto-create users on first SAML login'],
              ['Group-to-role mapping', 'Planned', 'Map IdP groups to LogPulse roles (Owner, Admin, Member, Viewer)'],
              ['Supported IdPs', 'Planned', 'Okta, Azure AD (Entra ID), OneLogin, Google Workspace'],
            ]}
          />
        </Section>

        <Section id="api-keys" title="API Keys">
          <P>
            API keys provide programmatic access to the LogPulse API. Each key is scoped to
            specific permissions and can be rotated or revoked at any time without affecting
            other keys.
          </P>

          <SubSection id="key-scopes" title="Key Scopes">
            <Table
              headers={['Scope', 'Permissions', 'Use Case']}
              rows={[
                ['ingest-only', 'POST /api/v1/logs, POST /api/v1/logs/batch', 'Vector agents, application logging, CI/CD pipelines'],
                ['read-only', 'GET /api/v1/logs, GET /api/v1/search, GET /api/v1/alerts', 'Dashboards, monitoring integrations, read-only tooling'],
                ['full-access', 'All API endpoints including management operations', 'Admin scripts, automation, infrastructure-as-code'],
              ]}
            />
          </SubSection>

          <SubSection id="key-management" title="Creating and Managing Keys">
            <P>
              To create an API key, navigate to Integrations, then HTTP API, then click Create API
              Key. Provide a name, select the scope, and optionally set an expiration date.
              The key is displayed once after creation and cannot be retrieved later.
            </P>
            <CodeBlock title="API key format">
{`lp_live_a1b2c3d4e5f6g7h8i9j0   (production key)
lp_test_a1b2c3d4e5f6g7h8i9j0   (test/staging key)`}
            </CodeBlock>
            <P>
              Keys can be rotated by creating a new key, updating your configuration to use
              the new key, and then revoking the old key. LogPulse does not support in-place
              key rotation -- this two-step process ensures zero-downtime key changes.
            </P>
          </SubSection>

          <SubSection id="key-revocation" title="Revoking Keys">
            <P>
              To revoke a key, click the Revoke button on the Integrations → HTTP API page. Revocation takes
              effect immediately. Any requests using the revoked key will receive a 401
              Unauthorized response. Revoked keys cannot be reinstated.
            </P>
            <Callout type="warning">
              Revoking an active key immediately breaks all integrations using that key. Before
              revoking, ensure all services have been updated to use a replacement key.
            </Callout>
          </SubSection>
        </Section>

        <Section id="team-management" title="Team Management">
          <SubSection id="inviting-members" title="Inviting Members">
            <P>
              To invite a new team member, navigate to Settings, then Team. Click Invite Member,
              enter the user's email address, and select a role. The user receives an email
              invitation with a link to join the organization.
            </P>
            <P>
              Invitations expire after 48 hours. If the user has not accepted the invitation, you
              can resend it from the pending invitations list.
            </P>
          </SubSection>

          <SubSection id="roles" title="Roles">
            <P>
              LogPulse uses four predefined roles. Each user is assigned exactly one role within
              an organization. Roles cannot be customized, but the permissions they grant cover
              common access patterns.
            </P>
            <Table
              headers={['Role', 'Description']}
              rows={[
                ['Owner', 'Full access to all features including billing, organization settings, and member management. Each organization must have at least one Owner.'],
                ['Admin', 'Can manage alert rules, notification channels, dashboards, saved searches, and team members. Cannot access billing or delete the organization.'],
                ['Member', 'Can search logs, write logs, create and update alerts, create and update dashboards, and manage pipelines. Cannot manage team settings, API keys, or delete alerts.'],
                ['Viewer', 'Read-only access to logs, dashboards, roles, and pipelines. Cannot create or modify any resources.'],
              ]}
            />
          </SubSection>
        </Section>

        <Section id="role-permissions" title="Role Permissions">
          <P>
            The following table shows the complete permissions matrix for each role.
          </P>
          <Table
            headers={['Permission', 'Owner', 'Admin', 'Member', 'Viewer']}
            rows={[
              ['Read logs', 'Yes', 'Yes', 'Yes', 'Yes'],
              ['Write logs', 'Yes', 'Yes', 'Yes', 'No'],
              ['Delete / export logs', 'Yes', 'Yes', 'No', 'No'],
              ['Read dashboards', 'Yes', 'Yes', 'Yes', 'Yes'],
              ['Create / update dashboards', 'Yes', 'Yes', 'Yes', 'No'],
              ['Delete dashboards', 'Yes', 'Yes', 'No', 'No'],
              ['Create / update alerts', 'Yes', 'Yes', 'Yes', 'No'],
              ['Delete alerts', 'Yes', 'Yes', 'No', 'No'],
              ['Create / read API keys', 'Yes', 'Yes', 'No', 'No'],
              ['Revoke API keys', 'Yes', 'Yes', 'No', 'No'],
              ['Read roles', 'Yes', 'Yes', 'Yes', 'Yes'],
              ['Manage roles', 'Yes', 'Yes', 'No', 'No'],
              ['Manage pipelines', 'Yes', 'Yes', 'Yes', 'Read only'],
              ['Invite / remove users', 'Yes', 'Yes', 'No', 'No'],
              ['Change member roles', 'Yes', 'Yes', 'No', 'No'],
              ['Manage organization settings', 'Yes', 'Yes', 'No', 'No'],
              ['Manage billing', 'Yes', 'No', 'No', 'No'],
            ]}
          />
        </Section>

        <Section id="organizations" title="Organizations">
          <SubSection id="creating-orgs" title="Creating Organizations">
            <P>
              An organization is the top-level container for all LogPulse resources including
              logs, dashboards, alerts, and team members. When you sign up, a default
              organization is created automatically. You can create additional organizations
              from the organization switcher in the top navigation bar.
            </P>
            <P>
              Each organization has its own billing, data storage, API keys, and team roster.
              Data is fully isolated between organizations -- there is no cross-organization
              log access. Each user can create up to 5 organizations, and each organization
              can have up to 50 members.
            </P>
          </SubSection>

          <SubSection id="switching-orgs" title="Switching Organizations">
            <P>
              If you belong to multiple organizations, use the organization switcher in the
              top-left corner of the dashboard to move between them. Your role may differ
              across organizations (for example, Owner in one and Member in another).
            </P>
          </SubSection>

          <SubSection id="org-settings" title="Organization-Level Settings">
            <P>
              Organization settings are accessible to Owners and include:
            </P>
            <Table
              headers={['Setting', 'Description']}
              rows={[
                ['Organization name', 'Display name shown in the dashboard and notifications'],
                ['Default timezone', 'Timezone used for dashboard displays and scheduled reports'],
                ['Data retention policy', 'Default retention period for all indexes'],
                ['OAuth providers', 'Google and GitHub OAuth configuration'],
                ['Billing plan', 'Current plan, usage, and payment method'],
              ]}
            />
          </SubSection>
        </Section>

        <Section id="audit-log" title="Audit Log">
          <P>
            The audit log records all administrative and security-relevant actions performed
            within your organization. It provides a tamper-evident trail for compliance audits
            and security investigations.
          </P>
          <P>
            The following events are recorded in the audit log:
          </P>
          <Table
            headers={['Category', 'Events']}
            rows={[
              ['Authentication', 'Login success, login failure, password reset, OAuth login, email verification'],
              ['Team Management', 'Member invited, member role changed, member removed, invitation resent, invitation revoked'],
              ['API Keys', 'Key created, key revoked, key used (first use only)'],
              ['Alert Rules', 'Rule created, rule updated, rule deleted, rule enabled/disabled'],
              ['Dashboards', 'Dashboard created, dashboard deleted, dashboard shared'],
              ['Organization', 'Settings updated, plan changed'],
            ]}
          />
          <P>
            Each audit log entry includes the timestamp, actor (user email or API key ID),
            action, resource affected, source IP address, and user agent. Audit logs are
            retained for 365 days regardless of your data retention plan.
          </P>
          <CodeBlock title="Example audit log entry">
{`{
  "timestamp": "2026-03-21T14:32:10.000Z",
  "actor": "admin@company.com",
  "action": "alert_rule.created",
  "resource": "rule_high_error_rate",
  "resource_type": "alert_rule",
  "ip_address": "203.0.113.42",
  "user_agent": "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)",
  "details": {
    "rule_name": "High Error Rate",
    "severity": "warning",
    "channels": ["ch_slack_ops"]
  }
}`}
          </CodeBlock>
        </Section>

        <Section id="session-management" title="Session Management">
          <P>
            LogPulse session management provides controls for session duration, concurrent
            sessions, and forced logout.
          </P>
          <Table
            headers={['Setting', 'Default', 'Configurable']}
            rows={[
              ['Session duration', '7 days', 'Configured via BETTER_AUTH_SECRET'],
              ['Session update age', '24 hours', 'Session refreshed daily'],
              ['Cookie cache', '5 minutes', 'Cookie validation caching period'],
              ['Concurrent sessions', 'Unlimited', 'No limit enforced'],
            ]}
          />
          <P>
            Sessions are managed by Better Auth and expire after 7 days. The session token is
            refreshed every 24 hours. Cookie validation is cached for 5 minutes to reduce
            database lookups. When a session expires, the user is redirected to the login page.
            Secure cookies are used automatically in production environments.
          </P>
        </Section>

        <Section id="security-best-practices" title="Security Best Practices">
          <P>
            Follow these recommendations to secure your LogPulse organization:
          </P>
          <P>
            Use OAuth social login where possible. Encourage team members to sign in using
            Google or GitHub OAuth, which delegates authentication security (including MFA)
            to the identity provider.
          </P>
          <P>
            Rotate API keys regularly. Establish a key rotation schedule (recommended: every 90
            days) and automate the rotation process where possible. Use separate keys for each
            service or environment to limit blast radius.
          </P>
          <P>
            Apply the principle of least privilege. Assign users the minimum role required for
            their responsibilities. Use ingest-only API keys for logging agents and read-only
            keys for dashboards and monitoring tools.
          </P>
          <P>
            Review the audit log regularly. Set up a monthly review of audit log entries for
            unusual patterns: failed login attempts, API key creation from unexpected IPs,
            or role changes.
          </P>
          <P>
            Use OAuth for teams. Google and GitHub OAuth centralizes authentication and
            delegates credential management and MFA to the identity provider.
          </P>
          <Callout type="tip">
            Start with a security baseline: use OAuth sign-in, rotate API keys every 90 days,
            and apply least-privilege roles. These practices
            address the most common security risks.
          </Callout>
        </Section>

        <Section id="enterprise-access" title="Enterprise Access Controls (Roadmap)">
          <P>
            The following enterprise access controls are planned for the Enterprise plan.
            These features provide the governance and auditability required for large organizations
            and regulated environments.
          </P>

          <SubSection id="scim" title="SCIM Provisioning">
            <P>
              SCIM (System for Cross-domain Identity Management) enables automatic user lifecycle
              management from your identity provider. When a user is added or removed in your IdP
              (Okta, Azure AD, etc.), the change is automatically reflected in LogPulse -- no
              manual user management required.
            </P>
            <Table
              headers={['Operation', 'Behavior']}
              rows={[
                ['Create user', 'User account created with role from group mapping'],
                ['Update user', 'Profile attributes synced (name, email, groups)'],
                ['Deactivate user', 'Account disabled; active sessions revoked; API keys suspended'],
                ['Delete user', 'Account removed; owned resources transferred to org Owner'],
              ]}
            />
          </SubSection>

          <SubSection id="jit-access" title="Just-in-Time Access">
            <P>
              Just-in-Time (JIT) access provides time-bounded elevated permissions for incident
              response and debugging. Instead of permanently granting broad access, team members
              can request elevated roles that automatically expire after a configurable duration
              (30 minutes to 24 hours).
            </P>
            <Callout type="info">
              JIT access is planned as an Enterprise feature. It integrates with the audit log
              to provide a complete trail of who had elevated access and when.
            </Callout>
          </SubSection>

          <SubSection id="ip-allowlists" title="IP Allowlists">
            <P>
              IP allowlists restrict access to the LogPulse dashboard and API to specific IP
              addresses or CIDR ranges. When configured, requests from non-allowlisted IPs are
              rejected with HTTP 403. IP allowlists apply to both browser-based dashboard access
              and API calls, and can be configured per organization.
            </P>
            <Callout type="info">
              IP allowlists are planned and not available yet.
            </Callout>
          </SubSection>

          <SubSection id="cmk" title="Customer-Managed Keys">
            <P>
              Customer-Managed Keys (CMK) allow you to control the encryption keys used to protect
              your log data at rest. LogPulse uses AWS KMS for key management. With CMK enabled,
              you provide your own KMS key ARN and LogPulse uses it for all data encryption
              operations for your organization. Revoking the key immediately renders your data
              inaccessible.
            </P>
            <Callout type="info">
              CMK is planned for the Enterprise plan and not available yet.
            </Callout>
          </SubSection>

          <SubSection id="audit-export" title="Audit Log Export">
            <P>
              Audit log export allows you to stream or batch-export all audit events to an external
              system for long-term retention, SIEM integration, or compliance archival. Exported
              events include full metadata: timestamp, actor, action, resource, IP address, and
              user agent.
            </P>
            <P>
              A dedicated export is not available yet. On every plan, audit events are stored in
              the <code>_audit</code> index: search them with <code>index=_audit</code>, or pull
              them through the search API with a personal access token.
            </P>
            <Table
              headers={['Export Method', 'Status']}
              rows={[
                ['Search and the search API (index=_audit)', 'Available on every plan'],
                ['CSV on-demand export', 'Planned'],
                ['JSON scheduled export', 'Planned'],
                ['Real-time SIEM streaming (webhook)', 'Planned'],
                ['S3 archival export', 'Planned'],
              ]}
            />
          </SubSection>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
